import React, { useEffect, useRef, useState } from "react";
import { useSheetBack } from "../lib/sheetStack";

/* THE shared customer bottom sheet: backdrop, slide-up panel, drag handle + swipe-down,
   scroll lock, safe area, dialog semantics. History/Back/Escape is owned by sheetStack. */
export default function BottomSheet({
  open,
  onClose,
  label,
  dismissible = true,
  closeOnBackdrop,
  closeOnSwipe,
  closeOnEscape,
  closeOnBack,
  maxHeight = "max-h-[88vh]",
  zIndex = 50,
  testId,
  panelClassName = "",
  children,
}) {
  const allow = (v) => (dismissible === false ? false : v !== false);
  const backdropOk = allow(closeOnBackdrop);
  const swipeOk = allow(closeOnSwipe);

  useSheetBack(open, onClose, { closeOnBack: allow(closeOnBack), closeOnEscape: allow(closeOnEscape) });

  const panelRef = useRef(null);
  const dragRef = useRef({ y: 0, t: 0, active: false });
  const [dragY, setDragY] = useState(0);
  const [entered, setEntered] = useState(false);

  // Background scroll lock (iOS Safari safe) + exact restore. Focus moves in and back out.
  useEffect(() => {
    if (!open) return undefined;
    const trigger = document.activeElement;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      position: body.style.position, top: body.style.top, left: body.style.left,
      right: body.style.right, width: body.style.width, overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    const focusTimer = setTimeout(() => panelRef.current?.focus({ preventScroll: true }), 50);
    const enterTimer = setTimeout(() => setEntered(true), 320);
    return () => {
      clearTimeout(focusTimer);
      clearTimeout(enterTimer);
      setEntered(false);
      setDragY(0);
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.left = prev.left;
      body.style.right = prev.right;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
      if (trigger && typeof trigger.focus === "function") trigger.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  const scrollableAt = (node) => {
    let el = node;
    while (el && el !== panelRef.current) {
      const oy = window.getComputedStyle(el).overflowY;
      if ((oy === "auto" || oy === "scroll") && el.scrollHeight > el.clientHeight + 1) return el;
      el = el.parentElement;
    }
    return null;
  };

  const onTouchStart = (e) => {
    if (!swipeOk || e.touches.length !== 1) return;
    const touch = e.touches[0];
    const onHandle = !!(e.target.closest && e.target.closest("[data-sheet-handle]"));
    const sc = scrollableAt(e.target);
    dragRef.current = { y: touch.clientY, t: Date.now(), active: onHandle || !sc || sc.scrollTop <= 0 };
  };
  const onTouchMove = (e) => {
    if (!dragRef.current.active) return;
    const dy = e.touches[0].clientY - dragRef.current.y;
    setDragY(dy > 6 ? dy : 0);
  };
  const onTouchEnd = () => {
    if (!dragRef.current.active) return;
    const dy = dragY;
    const dt = Math.max(1, Date.now() - dragRef.current.t);
    dragRef.current.active = false;
    if (dy > 110 || (dy > 40 && dy / dt > 0.5)) onClose();
    else setDragY(0);
  };

  return (
    <div className="fixed inset-0 max-w-md mx-auto flex items-end" style={{ zIndex }} data-testid={testId}>
      <div className="absolute inset-0 bg-black/50" data-testid={testId ? `${testId}-backdrop` : undefined}
        onClick={backdropOk ? onClose : undefined} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label} tabIndex={-1}
        data-testid={testId ? `${testId}-panel` : undefined}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onTouchCancel={onTouchEnd}
        className={`relative w-full bg-white rounded-t-3xl flex flex-col outline-none ${maxHeight} ${entered ? "" : "animate-slide-up"} ${panelClassName}`}
        style={{
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          transform: dragY ? `translateY(${dragY}px)` : undefined,
          transition: dragRef.current.active ? "none" : "transform 0.25s ease",
        }}>
        {swipeOk && (
          <div data-sheet-handle className="shrink-0 pt-2.5 pb-1 flex justify-center cursor-grab" aria-hidden="true"
            data-testid={testId ? `${testId}-handle` : undefined}>
            <span className="block w-10 h-1.5 rounded-full bg-slate-300" />
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
